import { AssertionReportChangedAssertion } from '@/components/assertion-report/AssertionReportChangedAssertion';
import { AssertionReportStats } from '@/components/assertion-report/AssertionReportStats';
import { AssertionReportUnchangedAssertion } from '@/components/assertion-report/AssertionReportUnchangedAssertion';
import { GridBackground } from '@/components/common/GridBackground';
import { useAssertionValidationReport } from '@/lib/hooks/use-assertion-validation-process';
import { cn } from '@/lib/utils';
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import * as v from 'valibot';

export const Route = createFileRoute(
  '/_authenticated/assertion/report/$reportId',
)({
  validateSearch: v.object({
    simplified: v.optional(v.boolean()),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { reportId } = useParams({
    from: '/_authenticated/assertion/report/$reportId',
  });
  const { simplified } = useSearch({
    from: '/_authenticated/assertion/report/$reportId',
  });

  const {
    data: report,
    isLoading,
    error,
  } = useAssertionValidationReport(reportId);

  if (isLoading) {
    return (
      <div className="min-h-screen w-full p-4 flex items-center justify-center">
        <div>Chargement du rapport...</div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen w-full p-4 flex items-center justify-center">
        <div>Erreur lors du chargement du rapport</div>
      </div>
    );
  }

  const totalAssertions =
    report.changedAssertions.length + report.unchangedAssertions.length;

  return (
    <div
      className={cn('min-h-screen w-full p-4 relative', {
        'bg-[#FEFAEF]': !simplified,
      })}
    >
      {!simplified && <GridBackground />}

      <div className="relative">
        <main
          className={cn('container mx-auto mt-8', {
            'mt-4': simplified,
          })}
        >
          <div
            className={cn('p-4 sm:p-6 space-y-3', {
              'bg-white border border-[#C4B3E8] rounded-lg shadow-sm':
                !simplified,
            })}
          >
            <h1 className="font-plein font-bold text-[20px] sm:text-[22px] leading-[1.4] sm:leading-[1.27] tracking-[-2.5%] text-[#2D0A50]">
              Résultats de validation du{' '}
              {typeof report.finalizedAt === 'string'
                ? new Date(report.finalizedAt).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })
                : report.finalizedAt}
            </h1>

            <AssertionReportStats
              total={totalAssertions}
              unchanged={report.unchangedAssertions.length}
              changed={report.changedAssertions.length}
            />

            {report.changedAssertions.map((assertion, index) => (
              <div key={assertion.task.assertionId}>
                <h2
                  className={cn(
                    'mt-10 mb-5 font-plein font-bold text-[20px] sm:text-[22px] leading-[1.4] sm:leading-[1.27] tracking-[-2.5%] text-[#2D0A50]',
                  )}
                >
                  Affirmations modifiées ({index + 1} /{' '}
                  {report.changedAssertions.length})
                </h2>
                <AssertionReportChangedAssertion entry={assertion} />
              </div>
            ))}

            {report.unchangedAssertions.length > 0 && (
              <>
                <h2 className="mt-10 mb-5 font-plein font-bold text-[20px] sm:text-[22px] leading-[1.4] sm:leading-[1.27] tracking-[-2.5%] text-[#2D0A50]">
                  Affirmations non modifiées
                </h2>
                {report.unchangedAssertions.map((assertion, index) => (
                  <AssertionReportUnchangedAssertion
                    key={assertion.assertionId}
                    entry={assertion}
                    insertPageBreak={
                      index !== 0 &&
                      (index === 18 || (index !== 20 && index % 20 === 0))
                    }
                  />
                ))}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
